// src/components/CoachDashboard.js

import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import api from '../api';
import { useAuthContext } from '../context/AuthContext';
import {
  IoBarChartOutline,
  IoFlameOutline,
  IoWarningOutline,
  IoFitnessOutline,
  IoNutritionOutline,
  IoPeopleOutline,
  IoTrendingUpOutline,
  IoChevronForwardOutline,
  IoTimeOutline,
  IoCheckmarkCircleOutline,
  IoPersonAddOutline,
  IoMailOpenOutline,
} from 'react-icons/io5';

// ── Stat Card ──────────────────────────────────────────────────
const StatCard = ({ icon: Icon, label, value, color, sub, badge }) => (
  <div className="glass-panel p-5 rounded-xl flex items-start gap-4">
    <div className={`w-12 h-12 rounded-xl flex items-center justify-center flex-shrink-0 ${color}`}>
      <Icon className="text-2xl text-white" />
    </div>
    <div className="flex-1 min-w-0">
      <p className="text-gray-400 text-sm">{label}</p>
      <div className="flex items-end gap-2">
        <p className="text-3xl font-bold text-white leading-tight">{value}</p>
        {badge != null && (
          <span className={`text-xs font-semibold rounded-full px-2 py-0.5 mb-1 ${
            badge > 0 ? 'bg-yellow-500/20 text-yellow-400' : 'bg-green-500/20 text-green-400'
          }`}>
            {badge > 0 ? `+${badge} pendientes` : 'Todo OK'}
          </span>
        )}
      </div>
      {sub && <p className="text-xs text-gray-500 mt-0.5">{sub}</p>}
    </div>
  </div>
);

// ── Compliance Ring ────────────────────────────────────────────
const ComplianceRing = ({ pct }) => {
  const r = 28;
  const circ = 2 * Math.PI * r;
  const dash = (pct / 100) * circ;
  const color = pct >= 70 ? '#22c55e' : pct >= 40 ? '#f59e0b' : '#ef4444';
  return (
    <div className="glass-panel p-5 rounded-xl flex items-center gap-4">
      <div className="relative w-16 h-16 flex-shrink-0">
        <svg width="64" height="64" viewBox="0 0 64 64" className="-rotate-90">
          <circle cx="32" cy="32" r={r} fill="none" stroke="#374151" strokeWidth="6" />
          <circle
            cx="32" cy="32" r={r} fill="none"
            stroke={color} strokeWidth="6"
            strokeDasharray={`${dash} ${circ}`}
            strokeLinecap="round"
            style={{ transition: 'stroke-dasharray 0.6s ease' }}
          />
        </svg>
        <span className="absolute inset-0 flex items-center justify-center text-sm font-bold text-white">{pct}%</span>
      </div>
      <div>
        <p className="text-gray-400 text-sm">Compliance semanal</p>
        <p className="text-white font-semibold leading-tight">
          {pct >= 70 ? 'Excelente 🔥' : pct >= 40 ? 'Regular' : 'Mejorar'}
        </p>
        <p className="text-xs text-gray-500 mt-0.5">clientes que entrenaron esta semana</p>
      </div>
    </div>
  );
};

// ── Quick Link Card ────────────────────────────────────────────
const QuickCard = ({ to, icon: Icon, title, desc, accent, badge }) => (
  <Link to={to} className="block group">
    <div className="glass-panel p-4 rounded-xl hover:border-marizan-red hover:-translate-y-1 transition-all duration-300 h-full flex items-center gap-3">
      <div className={`w-10 h-10 rounded-lg flex items-center justify-center flex-shrink-0 ${accent}`}>
        <Icon className="text-lg text-white" />
      </div>
      <div className="flex-1 min-w-0">
        <div className="flex items-center gap-2">
          <h3 className="font-semibold text-white group-hover:text-marizan-red transition-colors text-sm">{title}</h3>
          {badge > 0 && (
            <span className="text-xs bg-marizan-red text-white rounded-full px-1.5 py-0.5 font-bold">{badge}</span>
          )}
        </div>
        <p className="text-xs text-gray-500 truncate">{desc}</p>
      </div>
      <IoChevronForwardOutline className="text-gray-600 group-hover:text-marizan-red transition-colors flex-shrink-0 text-sm" />
    </div>
  </Link>
);

// ── Helpers ───────────────────────────────────────────────────
const relativeDate = (dateStr) => {
  const d = new Date(dateStr + 'T00:00:00');
  const today = new Date();
  today.setHours(0, 0, 0, 0);
  const diff = Math.round((today - d) / 86400000);
  if (diff === 0) return 'Hoy';
  if (diff === 1) return 'Ayer';
  return `Hace ${diff} días`;
};

const initial = (name) => name?.charAt(0).toUpperCase() || '?';
const AVATAR_COLORS = ['bg-blue-600', 'bg-purple-600', 'bg-green-600', 'bg-orange-500', 'bg-pink-600', 'bg-teal-600'];
const avatarColor = (id) => AVATAR_COLORS[id % AVATAR_COLORS.length];

// ──────────────────────────────────────────────────────────────
const CoachDashboard = () => {
  const { user } = useAuthContext();
  const [stats, setStats] = useState(null);
  const [activity, setActivity] = useState([]);
  const [assignedClients, setAssignedClients] = useState([]);
  const [inactive, setInactive] = useState([]);
  const [pending, setPending] = useState([]);
  const [adherence, setAdherence] = useState(null);
  const [loading, setLoading] = useState(true);

  const firstName = user?.name?.split(' ')[0] || user?.email?.split('@')[0] || 'Coach';

  // Función para cargar datos del dashboard
  const loadDashboard = async () => {
    try {
      const [statsRes, actRes, assignedRes, inactRes, pendRes, adherenceRes] = await Promise.allSettled([
        api.get('/admin/dashboard/stats'),
        api.get('/admin/dashboard/recent-activity?limit=12'),
        api.get('/coach/clients/assigned'),
        api.get('/admin/dashboard/clients-inactive?days=7'),
        api.get('/admin/dashboard/clients-pending'),
        api.get('/admin/dashboard/adherence-summary?window_days=14'),
      ]);

      if (statsRes.status === 'fulfilled') setStats(statsRes.value.data);
      if (actRes.status === 'fulfilled') setActivity(actRes.value.data || []);
      if (assignedRes.status === 'fulfilled') setAssignedClients(assignedRes.value.data || []);
      else setAssignedClients([]);
      if (inactRes.status === 'fulfilled') setInactive(inactRes.value.data || []);
      else setInactive([]);
      if (pendRes.status === 'fulfilled') setPending(pendRes.value.data || []);
      else setPending([]);
      if (adherenceRes.status === 'fulfilled') setAdherence(adherenceRes.value.data);
      else setAdherence(null);
    } catch (err) {
      console.error('Error cargando dashboard', err);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadDashboard();
    // Recargar cuando la página recupere el foco (por ejemplo, tras volver del detalle de cliente)
    const handleVisibility = () => {
      if (document.visibilityState === 'visible') {
        setLoading(true);
        loadDashboard();
      }
    };
    document.addEventListener('visibilitychange', handleVisibility);
    return () => document.removeEventListener('visibilitychange', handleVisibility);
  }, []);

  // Group recent activity by date
  const activityByDate = activity.reduce((acc, item) => {
    if (!acc[item.date]) acc[item.date] = [];
    acc[item.date].push(item);
    return acc;
  }, {});

  return (
    <div className="fade-up">
      {/* ── Header con saludo personalizado ── */}
      <div className="mb-7">
        <h1 className="text-3xl md:text-4xl font-display font-bold text-white mb-1">
          ¡Hola, {firstName}! 👋
        </h1>
        <p className="text-gray-400 text-sm">
          {new Date().toLocaleDateString('es-ES', { weekday: 'long', day: 'numeric', month: 'long', year: 'numeric' })}
        </p>
      </div>

      {/* ── Stat Cards row 1: clientes y actividad ── */}
      {loading ? (
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 mb-4">
          {[...Array(4)].map((_, i) => <div key={i} className="skeleton h-24 rounded-xl" />)}
        </div>
      ) : stats && (
        <>
          <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 mb-4">
            <StatCard
              icon={IoPeopleOutline}
              label="Clientes Activos"
              value={stats.total_active_clients}
              color="bg-blue-600"
              sub="membresías activas"
              badge={stats.total_pending_clients}
            />
            <StatCard
              icon={IoBarChartOutline}
              label="Entrenos esta semana"
              value={stats.total_logs_this_week}
              color="bg-marizan-red"
              sub="ejercicios registrados"
            />
            <StatCard
              icon={IoFlameOutline}
              label="Entrenaron hoy"
              value={stats.clients_trained_today}
              color="bg-orange-500"
              sub={`de ${stats.total_active_clients} activos`}
            />
            <StatCard
              icon={IoWarningOutline}
              label="Sin actividad 7d"
              value={stats.clients_inactive_7_days}
              color={stats.clients_inactive_7_days > 0 ? 'bg-yellow-600' : 'bg-green-600'}
              sub={stats.clients_inactive_7_days === 0 ? '¡Todos activos!' : 'requieren contacto'}
            />
          </div>

          {/* ── Row 2: compliance + quick counts ── */}
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-7">
            <ComplianceRing pct={stats.weekly_compliance_pct} />
            <div className="glass-panel p-5 rounded-xl flex items-center gap-4">
              <div className="w-12 h-12 rounded-xl bg-indigo-600 flex items-center justify-center flex-shrink-0">
                <IoFitnessOutline className="text-2xl text-white" />
              </div>
              <div>
                <p className="text-gray-400 text-sm">Programas de Entreno</p>
                <p className="text-3xl font-bold text-white">{stats.total_programs}</p>
                <Link to="/programs" className="text-xs text-indigo-400 hover:text-indigo-300 transition-colors">Ver todos →</Link>
              </div>
            </div>
            <div className="glass-panel p-5 rounded-xl flex items-center gap-4">
              <div className="w-12 h-12 rounded-xl bg-green-700 flex items-center justify-center flex-shrink-0">
                <IoNutritionOutline className="text-2xl text-white" />
              </div>
              <div>
                <p className="text-gray-400 text-sm">Planes de Nutrición</p>
                <p className="text-3xl font-bold text-white">{stats.total_nutrition_plans}</p>
                <Link to="/nutrition-plans" className="text-xs text-green-400 hover:text-green-300 transition-colors">Ver todos →</Link>
              </div>
            </div>
          </div>
        </>
      )}

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">

        {/* ── Feed de Actividad Reciente ── */}
        <div className="lg:col-span-2 space-y-4">
          <h2 className="text-base font-semibold text-white flex items-center gap-2">
            <IoTimeOutline className="text-marizan-red" />
            Actividad Reciente
          </h2>

          {loading ? (
            <div className="space-y-3">
              {[...Array(3)].map((_, i) => <div key={i} className="skeleton h-20 rounded-xl" />)}
            </div>
          ) : activity.length === 0 ? (
            <div className="glass-panel rounded-xl p-8 text-center text-gray-400 border-2 border-dashed border-gray-700">
              <IoFitnessOutline className="text-4xl mx-auto mb-2 opacity-40" />
              <p>Ningún cliente ha registrado entrenamientos aún.</p>
            </div>
          ) : (
            <div className="space-y-3">
              {Object.entries(activityByDate).map(([dateStr, items]) => {
                const byClient = items.reduce((a, i) => {
                  if (!a[i.client_id]) a[i.client_id] = { name: i.client_name, id: i.client_id, exercises: [] };
                  a[i.client_id].exercises.push(i);
                  return a;
                }, {});

                return (
                  <div key={dateStr} className="glass-panel rounded-xl overflow-hidden">
                    <div className="px-4 py-2 bg-white/5 border-b border-white/10 flex items-center justify-between">
                      <span className="text-sm font-medium text-gray-300">{relativeDate(dateStr)}</span>
                      <span className="text-xs text-gray-500">
                        {new Date(dateStr + 'T00:00:00').toLocaleDateString('es-ES', { day: 'numeric', month: 'short' })}
                      </span>
                    </div>
                    <div className="divide-y divide-white/5">
                      {Object.values(byClient).map(({ id, name, exercises }) => (
                        <Link
                          key={id}
                          to={`/clients/${id}`}
                          className="flex items-start gap-3 px-4 py-3 hover:bg-white/5 transition-colors group"
                        >
                          <div className={`w-9 h-9 rounded-full flex items-center justify-center text-sm font-bold flex-shrink-0 text-white ${avatarColor(id)}`}>
                            {initial(name)}
                          </div>
                          <div className="flex-1 min-w-0">
                            <p className="text-sm font-semibold text-white group-hover:text-marizan-red transition-colors">{name}</p>
                            <div className="flex flex-wrap gap-1 mt-1">
                              {exercises.slice(0, 3).map((ex, i) => (
                                <span key={i} className="inline-flex items-center gap-1 text-xs bg-white/10 text-gray-300 rounded-full px-2 py-0.5">
                                  <IoCheckmarkCircleOutline className="text-green-400 flex-shrink-0" />
                                  {ex.exercise_name}
                                  {ex.reps_completed && <span className="text-gray-500">×{ex.reps_completed}</span>}
                                </span>
                              ))}
                              {exercises.length > 3 && (
                                <span className="text-xs text-gray-500 px-1 py-0.5">+{exercises.length - 3} más</span>
                              )}
                            </div>
                            <p className="text-xs text-gray-600 mt-0.5">{exercises[0]?.routine_name}</p>
                          </div>
                          <IoChevronForwardOutline className="text-gray-600 group-hover:text-marizan-red transition-colors mt-1 flex-shrink-0" />
                        </Link>
                      ))}
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </div>

        {/* ── Columna derecha ── */}
        <div className="space-y-5">

          {/* Clientes asignados al coach */}
          {!loading && (
            <div className="glass-panel rounded-xl p-4 border border-white/10">
              <div className="flex items-center justify-between mb-3">
                <h2 className="text-base font-semibold text-white flex items-center gap-2">
                  <IoPeopleOutline className="text-blue-400" />
                  Mis Clientes Asignados
                </h2>
                <span className="text-xs bg-blue-500/20 text-blue-300 px-2 py-0.5 rounded-full font-semibold">
                  {assignedClients.length}
                </span>
              </div>

              {assignedClients.length === 0 ? (
                <p className="text-sm text-gray-400">Aún no tienes clientes asignados directamente.</p>
              ) : (
                <div className="space-y-2 max-h-56 overflow-auto pr-1">
                  {assignedClients.map((c) => (
                    <Link
                      key={c.id}
                      to={`/clients/${c.id}`}
                      className="flex items-center justify-between rounded-lg bg-white/5 hover:bg-white/10 px-3 py-2 transition-colors"
                    >
                      <div className="min-w-0">
                        <p className="text-sm text-white truncate">{c.name}</p>
                        <p className="text-xs text-gray-500 truncate">{c.email}</p>
                      </div>
                      <IoChevronForwardOutline className="text-gray-600" />
                    </Link>
                  ))}
                </div>
              )}

              {assignedClients.length > 8 && (
                <Link to="/clients" className="block text-xs text-blue-300 mt-3 hover:text-blue-200">
                  Ver todos en Clientes →
                </Link>
              )}
            </div>
          )}

          {/* Insight de adherencia */}
          {!loading && adherence && (
            <div className="glass-panel rounded-xl p-4 border border-white/10">
              <h2 className="text-base font-semibold text-white mb-3">Insight de Adherencia (14d)</h2>
              <div className="grid grid-cols-3 gap-2 mb-3 text-center">
                <div className="rounded-lg bg-green-500/15 border border-green-500/25 py-2">
                  <p className="text-lg font-bold text-green-300">{adherence.on_track}</p>
                  <p className="text-[11px] text-green-200">En Línea</p>
                </div>
                <div className="rounded-lg bg-yellow-500/15 border border-yellow-500/25 py-2">
                  <p className="text-lg font-bold text-yellow-300">{adherence.watch}</p>
                  <p className="text-[11px] text-yellow-200">Vigilancia</p>
                </div>
                <div className="rounded-lg bg-red-500/15 border border-red-500/25 py-2">
                  <p className="text-lg font-bold text-red-300">{adherence.at_risk}</p>
                  <p className="text-[11px] text-red-200">En Riesgo</p>
                </div>
              </div>

              <div className="space-y-2 max-h-56 overflow-auto pr-1">
                {(adherence.clients || []).slice(0, 5).map((c) => {
                  const statusLabel = c.status === 'at_risk' ? 'En Riesgo' : c.status === 'watch' ? 'Vigilancia' : 'En Línea';
                  return (
                    <Link
                      key={c.client_id}
                      to={`/clients/${c.client_id}`}
                      className="flex items-center justify-between rounded-lg bg-white/5 hover:bg-white/10 px-3 py-2 transition-colors"
                    >
                      <div className="min-w-0">
                        <p className="text-sm text-white truncate">{c.client_name}</p>
                        <p className="text-xs text-gray-400">{c.workouts_last_14d} días de entreno / 14 días</p>
                      </div>
                      <span className={`text-[10px] font-semibold px-2 py-1 rounded-full ${
                        c.status === 'at_risk'
                          ? 'bg-red-500/20 text-red-300'
                          : c.status === 'watch'
                            ? 'bg-yellow-500/20 text-yellow-300'
                            : 'bg-green-500/20 text-green-300'
                      }`}>
                        {statusLabel}
                      </span>
                    </Link>
                  );
                })}
              </div>
            </div>
          )}

          {/* Acciones rápidas */}
          <div>
            <h2 className="text-base font-semibold text-white flex items-center gap-2 mb-3">
              <IoTrendingUpOutline className="text-marizan-red" />
              Acciones Rápidas
            </h2>
            <div className="space-y-2">
              <QuickCard to="/clients" icon={IoPeopleOutline} title="Clientes" desc="Gestionar membresías y planes" accent="bg-blue-600" badge={stats?.total_pending_clients || 0} />
              <QuickCard to="/programs" icon={IoFitnessOutline} title="Programas" desc="Crear y editar rutinas" accent="bg-marizan-red" badge={0} />
              <QuickCard to="/nutrition-plans" icon={IoNutritionOutline} title="Nutrición" desc="Planes y dietas" accent="bg-green-700" badge={0} />
              <QuickCard to="/coach/reminder-templates" icon={IoMailOpenOutline} title="Plantillas" desc="Editar textos de recordatorios" accent="bg-rose-800" badge={0} />
            </div>
          </div>

          <Link to="/coach/reminders" className="glass-panel p-4 rounded-xl block hover:border-rose-500 transition-colors">
            <h2 className="text-base font-semibold text-white flex items-center gap-2">
              <IoMailOpenOutline className="text-rose-400" />
              Recordatorios
            </h2>
            <p className="text-xs text-gray-400 mt-1">Gestiona envíos manuales, lotes automáticos e historial desde un módulo dedicado.</p>
            <p className="text-xs text-rose-300 mt-2">Abrir módulo de recordatorios →</p>
          </Link>

          {/* Pendientes de activación */}
          {!loading && pending.length > 0 && (
            <div>
              <h2 className="text-base font-semibold text-white flex items-center gap-2 mb-3">
                <IoPersonAddOutline className="text-blue-400" />
                Pendientes de Activación
                <span className="bg-blue-500/20 text-blue-400 text-xs rounded-full px-2 py-0.5 font-bold">{pending.length}</span>
              </h2>
              <div className="glass-panel rounded-xl overflow-hidden border border-blue-900/40">
                <div className="divide-y divide-white/5">
                  {pending.slice(0, 4).map(c => (
                    <Link
                      key={c.client_id}
                      to={`/clients/${c.client_id}`}
                      className="flex items-center gap-3 px-4 py-3 hover:bg-white/5 transition-colors group"
                    >
                      <div className={`w-8 h-8 rounded-full flex items-center justify-center text-sm font-bold flex-shrink-0 text-white ${avatarColor(c.client_id)}`}>
                        {initial(c.client_name)}
                      </div>
                      <div className="flex-1 min-w-0">
                        <p className="text-sm font-medium text-white truncate group-hover:text-blue-400 transition-colors">{c.client_name}</p>
                        <p className="text-xs text-gray-500 truncate">{c.client_email}</p>
                      </div>
                      <IoChevronForwardOutline className="text-gray-600 group-hover:text-blue-400 transition-colors flex-shrink-0" />
                    </Link>
                  ))}
                </div>
                {pending.length > 4 && (
                  <Link to="/clients" className="block text-center text-xs text-gray-500 hover:text-white py-2 border-t border-white/5 transition-colors">
                    Ver todos ({pending.length})
                  </Link>
                )}
              </div>
            </div>
          )}

          {/* Sin actividad 7 días */}
          {!loading && inactive.length > 0 && (
            <div>
              <h2 className="text-base font-semibold text-white flex items-center gap-2 mb-3">
                <IoWarningOutline className="text-yellow-500" />
                Sin Actividad 7 Días
              </h2>
              <div className="glass-panel rounded-xl overflow-hidden border border-yellow-900/40">
                <div className="divide-y divide-white/5">
                  {inactive.slice(0, 4).map(c => (
                    <Link
                      key={c.client_id}
                      to={`/clients/${c.client_id}`}
                      className="flex items-center gap-3 px-4 py-3 hover:bg-white/5 transition-colors group"
                    >
                      <div className={`w-8 h-8 rounded-full flex items-center justify-center text-sm font-bold flex-shrink-0 text-white ${avatarColor(c.client_id)}`}>
                        {initial(c.client_name)}
                      </div>
                      <div className="flex-1 min-w-0">
                        <p className="text-sm font-medium text-white truncate group-hover:text-marizan-red transition-colors">{c.client_name}</p>
                        <p className="text-xs text-yellow-600">
                          {c.last_activity_date ? `${c.days_inactive} días sin entreno` : 'Sin entrenos aún'}
                        </p>
                      </div>
                      <IoChevronForwardOutline className="text-gray-600 group-hover:text-yellow-500 transition-colors flex-shrink-0" />
                    </Link>
                  ))}
                </div>
                {inactive.length > 4 && (
                  <Link to="/clients" className="block text-center text-xs text-gray-500 hover:text-white py-2 border-t border-white/5 transition-colors">
                    Ver todos ({inactive.length})
                  </Link>
                )}
              </div>
            </div>
          )}

          {/* Todo OK */}
          {!loading && inactive.length === 0 && pending.length === 0 && (
            <div className="glass-panel rounded-xl p-4 border border-green-900/40 text-center">
              <IoCheckmarkCircleOutline className="text-3xl text-green-500 mx-auto mb-1" />
              <p className="text-sm text-green-400 font-medium">¡Todo en orden!</p>
              <p className="text-xs text-gray-500 mt-0.5">Ningún cliente pendiente o inactivo</p>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default CoachDashboard;
